import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation } from 'react-router-dom';
import { useDownloadAssets } from '../DownloadAssetsContext';
import { uploadFiless } from 'components/Dam/Assets';
import { MODULE_IDS, useHasAccess } from 'actions/userActions';

const GlobalDropzoneContext = createContext(null);

/* ---------------- ALLOWED DAM ROUTES ---------------- */

const ALLOWED_DAM_ROUTES = ['/dam/v2/assets'];

export const GlobalDropzoneProvider = ({ children }) => {
  const location = useLocation();
  const { UploadFiles: UploadFilesContext } = useDownloadAssets();
  const [isDragging, setIsDragging] = useState(false);
  const hasDAMAccess = useHasAccess(MODULE_IDS.DAM);
  const ignoreNextDrop = useRef(false);

  // Whether the folder currently on screen accepts uploads from this user. Innerfolder
  // owns this call (it is what hides the Upload button) and pushes it down here, so a
  // page-level drop can never upload somewhere the Upload button refuses to.
  // Defaults to true for the assets root, where Innerfolder is not mounted.
  const [canUploadHere, setCanUploadHere] = useState(true);

  /* ---------- Helpers ---------- */

  const isDamRoute = useMemo(() => {
    return ALLOWED_DAM_ROUTES.some((route) => location.pathname.startsWith(route)) && hasDAMAccess;
  }, [location.pathname, hasDAMAccess]);

  const canDropOnPage = isDamRoute && canUploadHere;

  const getUploadFolderId = (path) => {
    if (!path) return null;

    const parts = path.split('/').filter(Boolean);
    const last = parts[parts.length - 1];

    // if ends with "assets" → root
    if (last === 'assets') return null;

    // otherwise last segment is folder id
    return last;
  };

  /* ---------- Dropzone ---------- */

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: !canDropOnPage, // 🔥 IMPORTANT
    onDragEnter: () => canDropOnPage && setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDrop: (acceptedFiles) => {
      if (!canDropOnPage) return;
      setIsDragging(false); // <-- move this to the top

      if (ignoreNextDrop.current) {
        ignoreNextDrop.current = false;
        return;
      }

      setIsDragging(false);
      const folderId = getUploadFolderId(location.pathname);
      uploadFiless(UploadFilesContext, acceptedFiles, folderId);
    },
  });

  return (
    <GlobalDropzoneContext.Provider
      value={{
        isDragActive,
        isDragging,
        isDamRoute,
        ignoreNextDrop,
        setIsDragging,
        setCanUploadHere
      }}
    >
      <div {...(canDropOnPage ? getRootProps() : {})} style={{ minHeight: '100vh' }}>
        {children}

        {/* -------- Global Overlay -------- */}
        {isDragging && canDropOnPage && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(33, 207, 214, 0.35)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <h2 style={{ color: '#fff', fontSize: 26 }}>Drop files to upload</h2>
          </div>
        )}
      </div>
    </GlobalDropzoneContext.Provider>
  );
};

export const useGlobalDropzone = () => useContext(GlobalDropzoneContext);
